<template>
  <div class="wb-print">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Weekly Syllabus Coverage</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ classBlocks.length }} Classes
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading weekly syllabus...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadData">Retry</b-button>
    </div>

    <div v-else-if="!classBlocks.length" class="text-center py-5">
      <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
      <h5>No weekly breakup found for selected filters.</h5>
    </div>

    <div
      v-for="(block, index) in classBlocks"
      :key="'class-' + (block.classID || index)"
      class="report-sheet"
      :class="{ 'page-break': index > 0 }"
    >
      <div class="letterhead">
        <img v-if="logo" :src="logo" alt="School logo" class="school-logo" />
        <div v-else class="school-logo school-logo--fallback">
          {{ campusInitials }}
        </div>
        <p v-if="showCampusNameText" class="school-name">
          {{ campusName }}
        </p>
        <p v-if="campusTagline" class="school-tagline">{{ campusTagline }}</p>

        <h2 class="report-title">{{ reportTitle(block) }}</h2>
        <p class="session-line">SESSION {{ sessionName || "—" }}</p>
      </div>

      <table class="meta-table">
        <tr>
          <th>Teacher's Name:</th>
          <td>{{ blockTeacherName(block) }}</td>
        </tr>
        <tr>
          <th>Class / Stage:</th>
          <td>{{ blockClassLabel(block) }}</td>
        </tr>
      </table>

      <table class="coverage-table">
        <thead>
          <tr>
            <th class="col-sno">S.No.</th>
            <th class="col-subject">Subject</th>
            <th class="col-topics">Topics Covered</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rIndex) in block.subjects"
            :key="(block.classID || index) + '-sub-' + (row.subjectID || rIndex)"
          >
            <td class="col-sno">{{ rIndex + 1 }}.</td>
            <td class="col-subject">{{ row.subjectName }}</td>
            <td class="col-topics">
              <ul v-if="topicList(row).length">
                <li v-for="(topic, tIndex) in topicList(row)" :key="tIndex">
                  {{ topic }}
                </li>
              </ul>
              <span v-else class="empty-topics">&nbsp;</span>
            </td>
          </tr>
          <tr v-if="!(block.subjects && block.subjects.length)">
            <td colspan="3" class="text-center empty-row">
              No subjects found for this class/week.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import { mapActions } from "vuex";

const BREAKUP_PRINT_LOGO =
  "https://cdn.cloudious.net/file-1790760172192-231502633.jpg";

export default {
  name: "WeeklyBreakupPrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: true,
      error: "",
      classBlocks: [],
      db: "",
      campusID: "",
      classIDs: "0",
      dtFrom: "",
      dtTo: "",
      sessionID: "0",
      tID: "",
      className: "",
      sessionName: "",
      campusName: "",
      campusTagline: "",
      teacherName: "",
      logo: BREAKUP_PRINT_LOGO,
    };
  },
  computed: {
    campusInitials() {
      return String(this.campusName || "S")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    },
    showCampusNameText() {
      // Full brand logos (e.g. Veritas wordmark) already include the name
      return !this.logo && !!(this.campusName || "").trim();
    },
  },
  async created() {
    this.readQuery();
    await this.loadData();
  },
  methods: {
    ...mapActions({ get: "get" }),

    readQuery() {
      const q = this.$route.query || {};
      this.db = q.db || this.$store.state.userData.db;
      this.campusID = q.campusID || this.$store.state.userData.cId;
      this.classIDs = q.classIDs || q.classID || "0";
      this.dtFrom = q.dtFrom || "";
      this.dtTo = q.dtTo || "";
      this.sessionID = q.sessionID || "0";
      this.tID = q.tID || "";
      this.className = q.className || "";
      this.sessionName = q.sessionName || "";
      this.campusName = q.campusName || this.$store.state.branchName || "";
      this.campusTagline = q.campusTagline || "";
      this.teacherName = q.teacherName || "";
      this.logo = BREAKUP_PRINT_LOGO;
    },

    formatOrdinalDay(date) {
      const day = date.getDate();
      const j = day % 10;
      const k = day % 100;
      let suffix = "th";
      if (j === 1 && k !== 11) suffix = "st";
      else if (j === 2 && k !== 12) suffix = "nd";
      else if (j === 3 && k !== 13) suffix = "rd";
      return day + suffix;
    },

    formatMonthYear(date) {
      return date.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    },

    formatDateRangeShort(fromStr, toStr) {
      const from = fromStr ? new Date(fromStr) : null;
      const to = toStr ? new Date(toStr) : null;
      if (!from || Number.isNaN(from.getTime())) return "";
      if (!to || Number.isNaN(to.getTime())) {
        return (
          this.formatOrdinalDay(from) + " " + this.formatMonthYear(from)
        ).toUpperCase();
      }
      const sameMonth =
        from.getMonth() === to.getMonth() &&
        from.getFullYear() === to.getFullYear();
      if (sameMonth) {
        return (
          this.formatOrdinalDay(from) +
          " - " +
          this.formatOrdinalDay(to) +
          " " +
          this.formatMonthYear(from)
        ).toUpperCase();
      }
      return (
        this.formatOrdinalDay(from) +
        " " +
        this.formatMonthYear(from) +
        " - " +
        this.formatOrdinalDay(to) +
        " " +
        this.formatMonthYear(to)
      ).toUpperCase();
    },

    reportTitle(block) {
      const range = this.formatDateRangeShort(
        (block && block.dtFrom) || this.dtFrom,
        (block && block.dtTo) || this.dtTo
      );
      if (range) return "WEEKLY SYLLABUS COVERAGE (" + range + ")";
      return "WEEKLY SYLLABUS COVERAGE";
    },

    blockClassLabel(block) {
      const className = (block && block.className) || this.className || "";
      const sectionName = (block && block.sectionName) || "";
      if (className && sectionName) return className + " / " + sectionName;
      return className || "—";
    },

    blockTeacherName(block) {
      const names = [];
      const seen = {};
      const pushName = (value) => {
        const n = String(value || "").trim();
        if (!n) return;
        const key = n.toLowerCase();
        if (seen[key]) return;
        seen[key] = true;
        names.push(n);
      };

      if (block && block.subjects && block.subjects.length) {
        block.subjects.forEach((s) => pushName(s && s.teacherName));
      }
      pushName(block && block.teacherName);
      pushName(this.teacherName);

      if (names.length) return names.join(", ");
      return "—";
    },

    mapSubjectRow(s) {
      return {
        subjectID: s.subjectID || s.id || 0,
        subjectName: s.subjectName || s.subject || "",
        tID: s.tID || s.teacherID || 0,
        teacherName: s.teacherName || s.teacher || "",
        topics: s.topics,
        classID: s.classID,
        className: s.className || "",
        sectionName: s.sectionName || "",
        dtFrom: s.dtFrom || "",
        dtTo: s.dtTo || "",
        weekLabel: s.weekLabel || "",
        dateRangeLabel: s.dateRangeLabel || "",
      };
    },

    groupSubjectsIntoClasses(subjects) {
      const map = new Map();
      (subjects || []).forEach((raw) => {
        const sub = this.mapSubjectRow(raw || {});
        const key = String(
          sub.classID != null && sub.classID !== "" ? sub.classID : "0"
        );
        if (!map.has(key)) {
          map.set(key, {
            classID: sub.classID,
            className: sub.className || this.className,
            sectionName: sub.sectionName || "",
            teacherName: sub.teacherName || "",
            dtFrom: sub.dtFrom || this.dtFrom,
            dtTo: sub.dtTo || this.dtTo,
            weekLabel: sub.weekLabel,
            dateRangeLabel: sub.dateRangeLabel,
            subjects: [],
          });
        }
        const cls = map.get(key);
        if (!cls.teacherName && sub.teacherName) {
          cls.teacherName = sub.teacherName;
        }
        if (!cls.className && sub.className) cls.className = sub.className;
        if (!cls.sectionName && sub.sectionName) {
          cls.sectionName = sub.sectionName;
        }
        cls.subjects.push(sub);
      });
      return Array.from(map.values());
    },

    formatDateRangeText(fromStr, toStr) {
      const from = fromStr ? new Date(fromStr) : null;
      const to = toStr ? new Date(toStr) : null;
      if (!from || Number.isNaN(from.getTime())) return "";
      if (!to || Number.isNaN(to.getTime())) {
        return (
          "Date: " +
          this.formatOrdinalDay(from) +
          " " +
          this.formatMonthYear(from)
        );
      }
      const sameMonth =
        from.getMonth() === to.getMonth() &&
        from.getFullYear() === to.getFullYear();
      if (sameMonth) {
        return (
          "Date: " +
          this.formatOrdinalDay(from) +
          " – " +
          this.formatOrdinalDay(to) +
          " " +
          this.formatMonthYear(from)
        );
      }
      return (
        "Date: " +
        this.formatOrdinalDay(from) +
        " " +
        this.formatMonthYear(from) +
        " – " +
        this.formatOrdinalDay(to) +
        " " +
        this.formatMonthYear(to)
      );
    },

    weekOfMonth(dateStr) {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return 1;
      return Math.floor((d.getDate() - 1) / 7) + 1;
    },

    defaultWeekLabel(fromStr) {
      const d = fromStr ? new Date(fromStr) : null;
      if (!d || Number.isNaN(d.getTime())) return "";
      const month = d.toLocaleDateString("en-GB", { month: "long" });
      return month + ": Week " + this.weekOfMonth(fromStr);
    },

    weekLabel(block) {
      return (
        block.weekLabel || this.defaultWeekLabel(block.dtFrom || this.dtFrom)
      );
    },

    dateRangeLabel(block) {
      return (
        block.dateRangeLabel ||
        this.formatDateRangeText(
          block.dtFrom || this.dtFrom,
          block.dtTo || this.dtTo
        )
      );
    },

    topicList(row) {
      if (Array.isArray(row.topics)) {
        return row.topics
          .map((t) => {
            if (typeof t === "string") return t.trim();
            return String((t && (t.topic || t.subTopic)) || "").trim();
          })
          .filter(Boolean);
      }
      if (typeof row.topics === "string") {
        return row.topics
          .split(/\r?\n|;/)
          .map((t) => t.trim())
          .filter(Boolean);
      }
      return [];
    },

    normalizeClasses(payload) {
      const top = payload && typeof payload === "object" ? payload : {};
      const raw =
        top.data !== undefined && top.data !== null ? top.data : top;

      const applyMeta = (obj) => {
        if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
        if (obj.sessionName) this.sessionName = obj.sessionName;
        if (obj.campusName) this.campusName = obj.campusName;
        if (obj.campusTagline) this.campusTagline = obj.campusTagline;
        if (obj.teacherName) this.teacherName = obj.teacherName;
      };
      applyMeta(top);
      applyMeta(raw);
      this.logo = BREAKUP_PRINT_LOGO;

      let classes = [];

      if (Array.isArray(raw)) {
        // Flat subject list OR class list with nested subjects
        if (raw.length && raw[0] && Array.isArray(raw[0].subjects)) {
          classes = raw;
        } else {
          classes = this.groupSubjectsIntoClasses(raw);
        }
      } else if (raw && Array.isArray(raw.classes)) {
        classes = raw.classes;
      } else if (raw && Array.isArray(raw.subjects)) {
        classes = this.groupSubjectsIntoClasses(raw.subjects);
      }

      return classes.map((cls) => {
        const subjects = (cls.subjects || []).map((s) => this.mapSubjectRow(s));
        let teacherName = cls.teacherName || cls.teacher || "";
        if (!teacherName) {
          const firstWithTeacher = subjects.find((s) => s.teacherName);
          teacherName = firstWithTeacher ? firstWithTeacher.teacherName : "";
        }
        return {
          classID: cls.classID,
          className: cls.className || cls.name || this.className || "",
          sectionName: cls.sectionName || "",
          teacherName,
          dtFrom: cls.dtFrom || this.dtFrom,
          dtTo: cls.dtTo || this.dtTo,
          weekLabel: cls.weekLabel || "",
          dateRangeLabel: cls.dateRangeLabel || "",
          subjects,
        };
      });
    },

    async loadData() {
      this.loading = true;
      this.error = "";
      try {
        let url =
          this.$store.state.domain +
          "WeeklyBreakup/GetReport?db=" +
          encodeURIComponent(this.db) +
          "&campusID=" +
          this.campusID +
          "&classIDs=" +
          encodeURIComponent(this.classIDs || "0") +
          "&dtFrom=" +
          encodeURIComponent(this.dtFrom || "") +
          "&dtTo=" +
          encodeURIComponent(this.dtTo || "") +
          "&sessionID=" +
          (this.sessionID || "0");

        if (this.tID) {
          url += "&tID=" + this.tID;
        }

        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        this.classBlocks = this.normalizeClasses(res);
      } catch (err) {
        console.error(err);
        this.error = "Failed to load weekly syllabus report.";
        this.classBlocks = [];
      } finally {
        this.loading = false;
      }
    },

    print() {
      window.print();
    },

    close() {
      window.close();
      this.$router.back();
    },
  },
};
</script>

<style lang="scss" scoped>
.wb-print {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-sheet {
  width: 210mm;
  min-height: 297mm;
  margin: 0 auto 1rem;
  background: #fff;
  border: 3px solid #0a2a5c;
  padding: 14mm 12mm;
  color: #000;
  box-sizing: border-box;
}

.page-break {
  page-break-before: always;
}

.letterhead {
  text-align: center;
  margin-bottom: 16px;
}

.school-logo {
  max-width: 200px;
  max-height: 110px;
  width: auto;
  height: auto;
  object-fit: contain;
  margin: 0 auto 12px;
  display: block;

  &--fallback {
    width: 78px;
    height: 78px;
    border-radius: 50%;
    background: #0a2a5c;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 24px;
  }
}

.school-name {
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #000;
  font-family: Georgia, "Times New Roman", serif;
  text-transform: uppercase;
}

.school-tagline {
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #000;
}

.report-title {
  margin: 10px 0 6px;
  font-size: 16px;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-transform: uppercase;
  color: #000;
  line-height: 1.35;
}

.session-line {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-transform: uppercase;
  color: #000;
}

.meta-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-bottom: 16px;

  th,
  td {
    border: 1.5px solid #000;
    padding: 9px 12px;
    font-size: 14px;
    vertical-align: middle;
    text-align: left;
  }

  th {
    width: 30%;
    background: #0a2a5c !important;
    color: #fff !important;
    font-weight: 800;
  }

  td {
    background: #fff;
    color: #0a2a5c;
    font-weight: 700;
  }
}

.coverage-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: 1.5px solid #000;
    padding: 10px 8px;
    vertical-align: top;
    color: #000;
  }

  th {
    text-align: center;
    font-weight: 800;
    background: #fff;
  }

  .col-sno {
    width: 10%;
    text-align: center;
    font-weight: 700;
  }

  .col-subject {
    width: 22%;
    font-weight: 800;
  }

  .col-topics {
    width: 68%;
    text-align: left;
    min-height: 48px;

    ul {
      margin: 0;
      padding-left: 18px;
    }

    li {
      margin-bottom: 4px;
    }
  }

  .empty-row {
    padding: 18px;
    color: #333;
    text-align: center;
  }
}
</style>

<style lang="scss">
@media print {
  @page {
    size: A4 portrait;
    margin: 8mm;
  }

  html,
  body {
    background: #fff !important;
    height: auto !important;
    overflow: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  .no-print,
  .main-menu,
  .v-nav-menu,
  .navbar,
  .navbar-container,
  .footer,
  .sidenav-overlay,
  .drag-target,
  .customizer,
  .Vue-Toastification__container {
    display: none !important;
  }

  .wb-print {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .app-content,
  .content-wrapper,
  .content-area-wrapper,
  .content-body {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .wb-print .report-sheet {
    width: 100% !important;
    min-height: auto !important;
    margin: 0 !important;
    padding: 8mm !important;
    border: 3px solid #0a2a5c !important;
    box-shadow: none !important;
    page-break-inside: avoid;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .wb-print .report-title,
  .wb-print .session-line {
    color: #000 !important;
  }

  .wb-print .meta-table th {
    background: #0a2a5c !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .wb-print .meta-table td {
    color: #0a2a5c !important;
  }

  .wb-print .meta-table th,
  .wb-print .meta-table td,
  .wb-print .coverage-table th,
  .wb-print .coverage-table td {
    border-color: #000 !important;
  }

  .wb-print .coverage-table th,
  .wb-print .coverage-table td {
    color: #000 !important;
  }

  .wb-print .page-break {
    page-break-before: always;
  }
}
</style>
